import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import kebabCase from 'lodash/kebabCase';
import { table, tableWithNestedTable } from '@atlaskit/adf-schema';
import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import { akEditorGutterPaddingDynamic } from '@atlaskit/editor-shared-styles';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { generateColgroup, generateColgroupFromNode, getResizerMinWidth } from '../pm-plugins/table-resizing/utils/colgroup';
import { TABLE_MAX_WIDTH } from '../pm-plugins/table-resizing/utils/consts';
import { getTableResizerContainerMaxWidthInCSS, getTableResizerContainerForFullPageWidthInCSS, getTableResizerItemWidthInCSS } from '../pm-plugins/table-resizing/utils/misc';
import { getAlignmentStyle } from './table-container-styles';
export var tableNodeSpecWithFixedToDOM = function tableNodeSpecWithFixedToDOM(config) {
  var tableNode = config.isNestingSupported ? tableWithNestedTable : table;
  return _objectSpread(_objectSpread({}, tableNode), {}, {
    toDOM: function toDOM(node) {
      var _node$attrs$width;
      var gutterPadding = function gutterPadding() {
        if (editorExperiment('platform_editor_preview_panel_responsiveness', true, {
          exposure: true
        })) {
          return 'calc(var(--ak-editor--large-gutter-padding) * 2)';
        } else {
          return "".concat(akEditorGutterPaddingDynamic() * 2, "px");
        }
      };
      var alignmentStyle = Object.entries(getAlignmentStyle(node.attrs.layout)).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          k = _ref2[0],
          v = _ref2[1];
        return "".concat(kebabCase(k), ": ").concat(kebabCase(v));
      }).join(';');
      var tableMinWidth = getResizerMinWidth(node);
      var tableWidthAttribute = node.attrs.width ? "".concat(node.attrs.width, "px") : "100%";
      var isFullPageEditor = !config.isChromelessEditor && !config.isCommentEditor;
      var attrs = _objectSpread({
        'data-number-column': node.attrs.isNumberColumnEnabled,
        'data-layout': node.attrs.layout,
        'data-autosize': node.attrs.__autoSize,
        'data-table-local-id': node.attrs.localId,
        'data-table-width': node.attrs.width
      }, expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) && {
        'data-ssr-placeholder': "table-".concat(node.attrs.localId),
        'data-ssr-placeholder-replace': "table-".concat(node.attrs.localId)
      });

      // This would be used for table scaling in colgroup CSS
      // cqw, or px is well supported
      var resizableTableWidth = expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) ? isFullPageEditor ? getTableResizerContainerForFullPageWidthInCSS(node, config.isTableScalingEnabled) : "calc(100cqw - calc(var(--ak-editor--large-gutter-padding) * 2))" : "min(calc(100cqw - calc(var(--ak-editor--large-gutter-padding) * 2)), ".concat((_node$attrs$width = node.attrs.width) !== null && _node$attrs$width !== void 0 ? _node$attrs$width : '100%', ")");
      var colgroup = '';
      if (config.allowColumnResizing) {
        if (expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true)) {
          colgroup = ['colgroup', {}].concat(_toConsumableArray(generateColgroupFromNode(node, config.isCommentEditor, config.isChromelessEditor, config.isNested, config.isTableScalingEnabled, config.shouldUseIncreasedScalingPercent)));
        } else {
          colgroup = ['colgroup', {}].concat(_toConsumableArray(generateColgroup(node)));
        }
      }

      // For Chromeless editor, and nested tables in full page editor
      var tableContainerDiv = ['div', {
        class: 'pm-table-container',
        'data-number-column': node.attrs.isNumberColumnEnabled,
        'data-layout': node.attrs.layout,
        'data-testid': 'table-container'
      }, ['div', {
        class: 'pm-table-sticky-sentinel-top',
        'data-testid': 'sticky-sentinel-top'
      }], ['div', {
        class: 'pm-table-row-controls-wrapper'
      }, ['div']], ['div', {
        class: 'pm-table-with-left-shadow',
        style: 'visibility: hidden'
      }], ['div', {
        class: 'pm-table-wrapper'
      }, ['table', attrs, ['span', {
        class: 'pm-table-shadow-sentinel-right'
      }], ['span', {
        class: 'pm-table-shadow-sentinel-left'
      }], colgroup, ['tbody', 0]]], ['div', {
        class: 'pm-table-with-right-shadow',
        style: 'visibility: hidden'
      }], ['div', {
        class: 'pm-table-sticky-sentinel-bottom',
        'data-testid': 'sticky-sentinel-bottom'
      }]];
      if (!config.tableResizingEnabled || expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) && config.isNested) {
        return ['div', {
          class: 'tableView-content-wrap',
          'data-prosemirror-initial-toDOM-render': 'true'
        }, tableContainerDiv];
      }
      var tableResizingDiv = ['div', {
        'data-testid': 'table-alignment-container',
        style: alignmentStyle
      }, ['div', {
        class: 'pm-table-resizer-container',
        style: convertToInlineCss({
          '--ak-editor-table-gutter-padding': config.isTableScalingEnabled ? 'calc(var(--ak-editor--large-gutter-padding) * 2)' : 'calc(var(--ak-editor--large-gutter-padding) * 2 - var(--ak-editor-resizer-handle-spacing))',
          '--ak-editor-table-width': resizableTableWidth,
          width: expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) ? "var(--ak-editor-table-width)" : "min(calc(100cqw - ".concat(gutterPadding(), "), ").concat(tableWidthAttribute, ")")
        })
      }, ['div', {
        class: 'resizer-item display-handle',
        style: convertToInlineCss({
          position: 'relative',
          userSelect: 'auto',
          boxSizing: 'border-box',
          '--ak-editor-table-max-width': "".concat(TABLE_MAX_WIDTH, "px"),
          '--ak-editor-table-min-width': "".concat(tableMinWidth, "px"),
          minWidth: 'var(--ak-editor-table-min-width)',
          maxWidth: expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) ? getTableResizerContainerMaxWidthInCSS(config.isCommentEditor, config.isChromelessEditor, config.isTableScalingEnabled) : "min(calc(100cqw - var(--ak-editor-table-gutter-padding)), var(--ak-editor-table-max-width))",
          width: expValEquals('platform_editor_tables_scaling_css', 'isEnabled', true) ? getTableResizerItemWidthInCSS(node, config.isCommentEditor, config.isChromelessEditor) : "min(calc(100cqw - var(--ak-editor-table-gutter-padding)), ".concat(tableWidthAttribute, ")")
        })
      }, ['span', {
        class: 'resizer-hover-zone'
      }, tableContainerDiv]]]];
      return ['div', {
        class: 'tableView-content-wrap',
        'data-prosemirror-initial-toDOM-render': 'true'
      }, tableResizingDiv];
    }
  });
};